import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Eye, EyeOff } from '@/components/icons';
import { useTheme } from '@/layout/useTheme';
import { apiError } from '@/lib/apiError';
import { api } from '@/api/client';
import { FullScreen } from './LoginPage';
import logoDark from '@/assets/logo-dark.svg';
import logoLight from '@/assets/logo-light.svg';

const INPUT = 'w-full rounded-lg border border-[var(--border-color)] bg-[var(--input-bg)] px-3 py-2.5 pr-10 text-sm text-[var(--text-primary)] outline-none focus:border-accent';
const LABEL = 'mb-1 block text-xs font-bold uppercase tracking-wide text-[var(--text-secondary)]';
const PRIMARY = 'block w-full rounded-lg bg-gradient-to-br from-accent to-[#e07b00] py-2.5 text-center text-sm font-bold text-white shadow transition hover:-translate-y-px hover:shadow-lg disabled:opacity-60';

/** Opened from the reset email: /reset-password?token=… — set a new password, then sign in. */
export function ResetPasswordPage() {
  const { theme } = useTheme();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(token ? null : 'This reset link is incomplete — open the link from the email again, or ask for a new one.');
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErr("The two passwords don't match."); return; }
    setBusy(true); setErr(null);
    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setDone(true);
    } catch (e2) {
      setErr(apiError(e2, 'Could not reset the password — ask for a new link.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <FullScreen>
      <form onSubmit={onSubmit} className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)]/60 p-8 shadow-xl backdrop-blur-xl">
        <div className="mb-5 flex justify-center">
          <img src={theme === 'dark' ? logoLight : logoDark} alt="Grhamoy" className="h-28 w-auto object-contain" />
        </div>
        <h1 className="mb-1 text-lg font-extrabold">Set a new password</h1>

        {done ? (
          <>
            <p className="mb-6 flex items-start gap-2 text-sm text-[var(--text-secondary)]">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-success" />
              Your password has been changed. Sign in with the new password.
            </p>
            <Link to="/login" replace className={PRIMARY}>Go to sign in</Link>
          </>
        ) : (
          <>
            <p className="mb-5 text-sm text-[var(--text-muted)]">At least 8 characters. This signs you out on other devices.</p>
            {err && <div className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-4 py-2 text-sm text-danger">{err}</div>}

            <label className={LABEL} htmlFor="new-password">New password</label>
            <div className="relative mb-4">
              <input id="new-password" type={show ? 'text' : 'password'} autoComplete="new-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT} required minLength={8} disabled={!token} />
              <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1} aria-label={show ? 'Hide passwords' : 'Show passwords'} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <label className={LABEL} htmlFor="confirm-password">Confirm new password</label>
            <input id="confirm-password" type={show ? 'text' : 'password'} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={`${INPUT} mb-6`} required disabled={!token} />

            <button type="submit" disabled={busy || !token || !password || !confirm} className={PRIMARY}>
              {busy ? 'Saving…' : 'Save new password'}
            </button>
            <p className="mt-4 text-center text-xs">
              <Link to="/login" className="font-semibold text-accent hover:underline">Back to sign in</Link>
            </p>
          </>
        )}
      </form>
    </FullScreen>
  );
}
